import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Search, ShieldCheck, TrendingUp, Repeat, LayoutGrid, Loader2, ArrowLeftRight, Coins, Package, Zap, ChevronDown, ChevronUp, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCategories } from '../../api/api';
import * as LucideIcons from 'lucide-react';

const Icon = ({ name, className }) => {
  const iconName = name ? name.split('-').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join('') : 'LayoutGrid';
  const LucideIcon = LucideIcons[iconName] || LucideIcons[name] || LayoutGrid;
  return <LucideIcon className={className} />;
};

function HeroFloatingIcon({ item, scrollY }) {
  const y = useTransform(scrollY, [0, 800], [item.y, item.y - 160]);
  const rotate = useTransform(scrollY, [0, 800], [0, item.rotateSpeed || 90]);
  const scale = useTransform(scrollY, [0, 400, 800], [1, 1.2, 0.8]);

  const springY = useSpring(y, { stiffness: 70, damping: 20 });
  const springRotate = useSpring(rotate, { stiffness: 70, damping: 20 });

  return (
    <motion.div
      className={`absolute left-1/2 top-1/2 ${item.color} opacity-40`}
      style={{
        x: item.x,
        y: springY,
        rotate: springRotate,
        scale: scale,
      }}
    >
      <div className="md:block hidden">
        <item.Icon size={item.size} />
      </div>
      <div className="md:hidden block">
        <item.Icon size={item.mobileSize} />
      </div>
    </motion.div>
  );
}

export default function HeroSection() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const { scrollY } = useScroll();

  const { data: categories = [], isLoading: isLoadingCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const mainCategories = Array.isArray(categories) 
    ? categories.filter(c => !c.parent && c.name !== 'services_main' && c.name !== 'other_main')
    : [];

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
        navigate(`/browse?keyword=${encodeURIComponent(searchValue)}`);
    }
  };

  const floatingIcons = [
    { Icon: ArrowLeftRight, color: "text-blue-500", x: -180, y: -80, size: 60, mobileSize: 40, rotateSpeed: 120 },
    { Icon: Coins, color: "text-amber-500", x: 190, y: -100, size: 70, mobileSize: 45, rotateSpeed: -140 },
    { Icon: Package, color: "text-emerald-500", x: -140, y: 120, size: 65, mobileSize: 40, rotateSpeed: 100 },
    { Icon: Repeat, color: "text-purple-500", x: 160, y: 100, size: 55, mobileSize: 35, rotateSpeed: -160 },
    { Icon: Zap, color: "text-orange-500", x: 0, y: -140, size: 45, mobileSize: 30, rotateSpeed: 180 },
  ];

  const [isExpandedCategories, setIsExpandedCategories] = useState(false);
  const displayCategories = isExpandedCategories ? mainCategories : mainCategories.slice(0, 9);

  return (
    <div className="relative flex flex-col items-center justify-center min-h-[50vh] bg-transparent pt-20">
      
      {/* Animated Background Icons Specific to Hero */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden flex items-center justify-center -z-10">
        <div className="relative w-full max-w-4xl h-full">
            {floatingIcons.map((item, index) => (
              <HeroFloatingIcon key={index} item={item} scrollY={scrollY} />
            ))}
        </div>
      </div>

      <div className="relative z-10 px-4 text-center w-full max-w-5xl mx-auto space-y-10">
        {/* Functional Search Bar - First Screen */}
        <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-3xl mx-auto w-full pt-4"
        >
            <h1 className="text-3xl md:text-5xl font-black bg-clip-text text-transparent bg-gradient-to-r from-primary via-purple-500 to-indigo-500 mb-4 drop-shadow-sm">
                {t('timeToUpgrade')}
            </h1>
            <h2 className="text-lg font-bold text-muted-foreground mb-8 uppercase tracking-widest">{t('searchItems')}</h2>
            
            <form onSubmit={handleSearch} className="relative group">
                <input 
                    type="text" 
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={t('searchItems')}
                    className="w-full h-16 md:h-20 ps-14 md:ps-16 pe-36 md:pe-52 rounded-3xl bg-card/90 backdrop-blur-md border-2 border-border focus:border-primary shadow-2xl focus:ring-8 focus:ring-primary/5 transition-all text-base md:text-xl outline-none"
                />
                <Search className="absolute start-5 md:start-6 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" size={24} />
                <button 
                    type="submit"
                    className="absolute end-2 top-2 bottom-2 md:end-2.5 md:top-2.5 md:bottom-2.5 px-4 md:px-10 rounded-2xl bg-primary text-primary-content font-black hover:bg-primary/90 transition-all shadow-lg active:scale-95 flex items-center gap-2 text-sm md:text-base"
                >
                    <Search size={18} className="hidden sm:block" />
                    <span>{t('navSearch')}</span>
                </button>
            </form>
        </motion.div>

        {/* Trust Badges - Placed ABOVE Categories */}
        <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap items-center justify-center gap-x-8 md:gap-x-12 gap-y-4 pt-6 border-t border-border/40 text-muted-foreground max-w-3xl mx-auto w-full"
        >
            <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-emerald-500/15 border border-emerald-500/20 shadow-sm"><ShieldCheck size={22} className="text-emerald-500" /></div>
                <div className="text-start">
                    <p className="text-[10px] font-black uppercase tracking-tighter leading-none mb-1 text-emerald-500">{t('secure')}</p>
                    <p className="text-xs font-bold text-foreground">{t('secureTrading')}</p>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-blue-500/15 border border-blue-500/20 shadow-sm"><TrendingUp size={22} className="text-blue-500" /></div>
                <div className="text-start">
                    <p className="text-[10px] font-black uppercase tracking-tighter leading-none mb-1 text-blue-500">{t('activeBadge')}</p>
                    <p className="text-xs font-bold text-foreground">{t('activeCommunity')}</p>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-2xl bg-purple-500/15 border border-purple-500/20 shadow-sm"><Repeat size={22} className="text-purple-500" /></div>
                <div className="text-start">
                    <p className="text-[10px] font-black uppercase tracking-tighter leading-none mb-1 text-purple-500">{t('fast')}</p>
                    <p className="text-xs font-bold text-foreground">{t('fastSwapping')}</p>
                </div>
            </div>
        </motion.div>

        {/* Dynamic Categories - Perfectly Balanced Rows */}
        <motion.div 
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="pt-2 max-w-3xl mx-auto w-full space-y-3"
        >
            <div className="grid grid-cols-2 sm:grid-cols-5 md:grid-cols-5 gap-2.5 sm:gap-3.5">
                {/* Show All */}
                <Link 
                    to="/browse"
                    className="flex flex-col items-center justify-center gap-2 px-2 py-3.5 md:px-3 md:py-4 rounded-2xl bg-card hover:bg-primary/10 hover:text-primary border border-border hover:border-primary/30 transition-all group shadow-sm hover:shadow-md text-center"
                >
                    <LayoutGrid size={26} className="group-hover:scale-125 transition-transform" />
                    <span className="text-[10px] md:text-[11px] font-black uppercase tracking-wider text-center line-clamp-1">{t('viewAll')}</span>
                </Link>

                {isLoadingCategories ? <Loader2 className="animate-spin col-span-full mx-auto" /> : displayCategories.map((cat) => (
                    <Link 
                        key={cat._id}
                        to={`/browse?category=${cat._id}`}
                        className="flex flex-col items-center justify-center gap-2 px-2 py-3.5 md:px-3 md:py-4 rounded-2xl bg-card hover:bg-primary/10 hover:text-primary border border-border hover:border-primary/30 transition-all group shadow-sm hover:shadow-md text-center"
                    >
                        <div className="text-xl md:text-2xl group-hover:scale-125 transition-transform">
                            <Icon name={cat.icon} className="w-6 h-6 md:w-7 md:h-7" />
                        </div>
                        <span className="text-[10px] md:text-[11px] font-black uppercase tracking-wider text-center line-clamp-1">
                            {cat[`label_${language}`] || cat.label_en}
                        </span>
                    </Link>
                ))}
            </div>

            {/* Explore All Categories Navigation Button */}
            <div className="pt-2 text-center flex justify-center">
                <Link
                    to="/browse"
                    className="group inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-card/80 hover:bg-card border border-border hover:border-primary/40 text-foreground hover:text-primary text-xs font-black uppercase tracking-wider transition-all shadow-xs hover:shadow-md active:scale-95"
                >
                    <span>{t('exploreAllCategories', 'Explore All Categories & Filters')}</span>
                    <ArrowRight size={16} className="group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform text-muted-foreground group-hover:text-primary" />
                </Link>
            </div>
        </motion.div>
      </div>
    </div>
  );
}